/**
 * `DrawingSet`: the drawings of one symbol root with transactional edits and undo / redo
 * (SPEC §12.5: "Undo/redo covers drawings (never orders)"). Pure; the web app wraps it with
 * persistence (`drawings/{root}` document) and change notification.
 *
 * Every edit is a transaction (`add` / `update` / `remove`); history stores the before / after
 * snapshot of each touched drawing, so undo and redo are exact and one drag, one clone or one
 * "remove all" is one step. A server copy adopted with `replaceAll` keeps the history: undo then
 * restores the snapshots it recorded, by id.
 */
import { MAX_DRAWINGS_PER_ROOT, type Drawing } from "./types.ts";

export type DrawingTx = { add?: readonly Drawing[]; update?: readonly Drawing[]; remove?: readonly string[] };

type Snap = Map<string, Drawing | null>;
type Entry = { before: Snap; after: Snap };

/** Undo depth. */
export const UNDO_LIMIT = 200;

export class DrawingSet {
  private readonly map = new Map<string, Drawing>();
  private undoStack: Entry[] = [];
  private redoStack: Entry[] = [];
  private sorted: Drawing[] | null = null;
  /** Bumped on every change (cheap change detection for subscribers). */
  rev = 0;

  constructor(list: readonly Drawing[] = []) {
    for (const d of list) this.map.set(d.id, d);
  }

  /** Drawings in z order (bottom first). The array is shared: do not mutate. */
  list(): readonly Drawing[] {
    this.sorted ??= [...this.map.values()].sort((a, b) => a.z - b.z);
    return this.sorted;
  }

  get(id: string): Drawing | undefined {
    return this.map.get(id);
  }

  get size(): number {
    return this.map.size;
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /**
   * Apply a transaction. Returns false when nothing changed (or an add would pass the per-root
   * cap). `history: false` = not undoable (server adoption uses `replaceAll`).
   */
  apply(tx: DrawingTx, opts: { history?: boolean } = {}): boolean {
    const before: Snap = new Map();
    const after: Snap = new Map();
    const adds = tx.add ?? [];
    if (this.map.size + adds.length > MAX_DRAWINGS_PER_ROOT) return false;
    for (const d of adds) {
      if (this.map.has(d.id)) continue;
      before.set(d.id, null);
      after.set(d.id, d);
    }
    for (const d of tx.update ?? []) {
      const cur = this.map.get(d.id);
      if (!cur || cur === d) continue;
      if (!before.has(d.id)) before.set(d.id, cur);
      after.set(d.id, d);
    }
    for (const id of tx.remove ?? []) {
      const cur = this.map.get(id);
      if (!cur) continue;
      if (!before.has(id)) before.set(id, cur);
      after.set(id, null);
    }
    if (after.size === 0) return false;
    this.write(after);
    if (opts.history !== false) {
      this.undoStack.push({ before, after });
      if (this.undoStack.length > UNDO_LIMIT) this.undoStack.shift();
      this.redoStack = [];
    }
    return true;
  }

  undo(): boolean {
    const e = this.undoStack.pop();
    if (!e) return false;
    this.write(e.before);
    this.redoStack.push(e);
    return true;
  }

  redo(): boolean {
    const e = this.redoStack.pop();
    if (!e) return false;
    this.write(e.after);
    this.undoStack.push(e);
    return true;
  }

  /** Adopt a server copy (another device / window): not an undo step. */
  replaceAll(list: readonly Drawing[]): void {
    this.map.clear();
    for (const d of list) this.map.set(d.id, d);
    this.changed();
  }

  private write(s: Snap): void {
    for (const [id, d] of s) {
      if (d === null) this.map.delete(id);
      else this.map.set(id, d);
    }
    this.changed();
  }

  private changed(): void {
    this.sorted = null;
    this.rev++;
  }
}
